import { Button } from "../../button";
import { Card } from "../../card";
import { Icon } from "../../icons";
import { summarizeInput } from "./summarize-input";

/**
 * An inline approval prompt (P2.2 / D5). When Chat runs in `ask` autonomy the
 * broker parks a mutation; this card lets the user allow / deny / always-allow
 * it without leaving the rail. "Always" allows every future call to the same
 * tool for the rest of the session (handled by the panel). The waiting
 * copilot_permission tool unblocks the moment the decision posts.
 */
export interface LivePermission {
  id: string;
  tool: string;
  input: unknown;
  risk: "read" | "mutate" | "forbidden";
  reason?: string;
}

export function PermissionCard({
  request,
  busy,
  onResolve,
}: {
  request: LivePermission;
  busy?: boolean;
  onResolve: (decision: "allow" | "deny", always: boolean) => void;
}) {
  const summary = summarizeInput(request.input);
  return (
    // Card surface with the approval tint layered on — keeps the elevation
    // ladder + radius from the shared primitive instead of a hand-rolled div.
    <Card className="gap-0 border-chart-1/40 bg-chart-1/5 p-2.5">
      <div className="mb-1.5 flex items-center gap-2">
        <Icon name="shield" size="sm" className="shrink-0 text-chart-1" />
        <span className="type-subtext text-foreground">Approve this action?</span>
      </div>
      <div className="mb-2 flex items-center gap-2">
        {/* badge-ignore: monospace tool-call identifier, not a status tag */}
        <span className="shrink-0 rounded bg-secondary/60 px-1.5 py-0.5 font-mono type-subtext text-foreground">
          {request.tool}
        </span>
        {summary ? (
          <span className="min-w-0 flex-1 truncate type-subtext text-muted-foreground">
            {summary}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="accept"
          disabled={busy}
          onClick={() => onResolve("allow", false)}
        >
          Allow
        </Button>
        <Button
          type="button"
          size="sm"
          variant="reject"
          disabled={busy}
          onClick={() => onResolve("deny", false)}
        >
          Deny
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => onResolve("allow", true)}
        >
          Always allow {request.tool}
        </Button>
      </div>
    </Card>
  );
}
